import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Grid, IconButton, Box, Typography, Card } from "@mui/material";
import {
  AddAPhoto as AddAPhotoIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

// Componentes SUI
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";

// API
import BucketApi from "api/bucketApi";
import ProcesosApi from "api/procesosApi";

const FirmaCliente = ({ procesoId }) => {
  const [firmaUrl, setFirmaUrl] = useState("");
  const [hasFirma, setHasFirma] = useState(false);
  const fileInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [procesoData, setProcesoData] = useState(null);
  const FILE_NAME = "firma_cliente.jpg";
  const ANEXO_TYPE = "FIRMA_CLIENTE";

  useEffect(() => {
    const fetchProcesoData = async () => {
      try {
        const response = await ProcesosApi.fetchProcesoById(procesoId);
        setProcesoData(response.data);
        if (response.data?.identificador) {
          await checkFirma(response.data.identificador);
        }
      } catch (error) {
        console.error("Error obteniendo datos del Proceso:", error);
      }
    };
    if (procesoId) {
      fetchProcesoData();
    }
  }, [procesoId]);

  const checkFirma = async (identificador) => {
    try {
      // Verificar existencia del archivo en el bucket
      const fileResponse = await BucketApi.checkSolicitudFileExists(FILE_NAME, identificador);
      // Verificar existencia del anexo
      const anexoResponse = await ProcesosApi.verificarAnexoProceso(procesoId, {
        tipo_anexo: ANEXO_TYPE,
      });
      // Considerar existente si están tanto el archivo como el anexo
      setHasFirma(fileResponse.data.exists && anexoResponse.data.exists);
      if (fileResponse.data.exists && anexoResponse.data.exists) {
        await fetchFirmaUrl(identificador);
      }
    } catch (error) {
      console.error("Error verificando firma:", error);
      setHasFirma(false);
    }
  };

  const fetchFirmaUrl = async (identificador) => {
    try {
      //const response = await BucketApi.getSolicitudFileUrl(FILE_NAME, identificador);
      const response = await BucketApi.getSolicitudFileUrlFresh(FILE_NAME, identificador);
      setFirmaUrl(response.data.file_url);
    } catch (error) {
      console.error("Error obteniendo URL de firma:", error);
      setFirmaUrl("");
    }
  };

  const handleFirmaChange = async (event) => {
    const file = event.target.files[0];
    if (!file || !procesoData?.identificador) return;

    setIsLoading(true);
    try {
      if (hasFirma) {
        const anexoResponse = await ProcesosApi.obtenerAnexoProcesoByTipo(procesoId, {
          tipo_anexo: ANEXO_TYPE,
        });
        const anexo = anexoResponse.data;
        if (anexo != undefined && !anexoResponse.data.error) {
          await ProcesosApi.eliminarAnexoProceso(procesoId, anexo.id);
        }
      }
      const formData = new FormData();
      formData.append("file", file);
      formData.append("file_name", FILE_NAME);
      formData.append("file_type", "solicitud");
      formData.append("proceso_id", procesoData.identificador);
      formData.append("registrar_anexo", "true");
      formData.append("tipo_anexo", ANEXO_TYPE);
      formData.append("nombre_anexo", "Firma Cliente");
      const response = await BucketApi.uploadFile(formData);
      if (response.data?.file_url) {
        await checkFirma(procesoData.identificador);
      }
    } catch (error) {
      console.error("Error subiendo firma:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteFirma = async () => {
    if (
      !procesoData?.identificador ||
      !window.confirm("¿Está seguro(a) de eliminar la Firma del Cliente?")
    )
      return;
    setIsLoading(true);
    try {
      const anexoResponse = await ProcesosApi.obtenerAnexoProcesoByTipo(procesoId, {
        tipo_anexo: ANEXO_TYPE,
      });
      const anexo = anexoResponse.data;
      if (anexo != undefined && !anexoResponse.data.error) {
        await ProcesosApi.eliminarAnexoProceso(procesoId, anexo.id);
      }
      setHasFirma(false);
      setFirmaUrl("");
    } catch (error) {
      console.error("Error eliminando firma:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <Card>
      <SuiBox p={2}>
        <SuiBox mb={1}>
          <SuiTypography variant="h6" fontWeight="medium">
            Firma Cliente
          </SuiTypography>
        </SuiBox>

        <Grid container spacing={3} alignItems="center" justifyContent="center">
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              {hasFirma ? (
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    maxWidth: 300,
                    height: 200,
                    backgroundColor: "#f8f9fa",
                    borderRadius: "8px",
                  }}
                >
                  <img
                    src={firmaUrl}
                    alt="Firma del cliente"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      borderRadius: "8px",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      display: "flex",
                      gap: 1,
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      padding: "4px",
                      borderRadius: "4px",
                    }}
                  >
                    <IconButton size="small" onClick={triggerFileInput} color="info">
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={handleDeleteFirma} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <Box
                  onClick={triggerFileInput}
                  sx={{
                    width: "100%",
                    maxWidth: 300,
                    height: 200,
                    border: "2px dashed #ccc",
                    borderRadius: "8px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    backgroundColor: "#f8f9fa",
                    transition: "all 0.2s",
                    "&:hover": {
                      borderColor: "primary.main",
                      backgroundColor: "#f0f0f0",
                    },
                  }}
                >
                  <AddAPhotoIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
                  <Typography color="textSecondary" align="center" sx={{ px: 2 }}>
                    Click para subir la firma del cliente
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                    Formatos permitidos: JPG, PNG
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFirmaChange}
          style={{ display: "none" }}
        />

        {isLoading && (
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Typography color="textSecondary">Procesando...</Typography>
          </Box>
        )}
      </SuiBox>
    </Card>
  );
};

FirmaCliente.propTypes = {
  procesoId: PropTypes.number.isRequired,
};

export default FirmaCliente;
